import { App, BlockedResourceError, NotFoundError, UserVerificationPendingError, AppStatusError, AppStatus, PaymentPendingError, DeploymentStatusError, AuthorizationError } from "@back4app2/sdk";
import PreDeployConfirmModal from './PreDeployConfirmModal';
import { RunAssistance } from '@back4app2/sdk';
import { useEffect, useRef, useState } from "react";
import back4app2 from "../back4app2";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import { ReactComponent as StatusSpinnerSVG } from '../assets/images/status-spinner.svg';
import { AmplitudeEvent, trackEvent } from "../utils/amplitude";
import { hasWritePermission } from "../utils/permissions";
import { DsButton } from "./ds";
import { ChevronDownIcon } from "./ds/icons";
import { TOAST_SURFACE } from "./ds/toast";
import { getAppStatusSummary } from '../utils/appStatus';
import RollbackDialog from "./Rollback/RollbackDialog";

/**
 * Runs the analysis, then decides what the Deploy button does.
 *
 * Wired only into the explicit Deploy actions, not into "save settings and
 * redeploy" or "domain changed, redeploy": someone who has just configured the
 * app does not need to be asked about its configuration, and a screen that
 * appears on every path is one nobody reads.
 */
const runPreDeploy = async (serviceEnvironmentId: string) => {
  try {
    return await back4app2.prepareDeployment(serviceEnvironmentId);
  } catch (e) {
    // Never block the deploy on the pre-check: it decides what to SHOW, and
    // the worker gate is what actually enforces. Failing here would turn an
    // analyzer problem into an inability to deploy.
    return { blocked: false, needsConfirmation: false };
  }
};

/**
 * Whether there is an action to offer at all. Where the answer is no the
 * component draws nothing, and the row under the status card closes up.
 */
export const canRunAppActions = (app: App): boolean => {
  const env = app.mainService && app.mainService.mainServiceEnvironment;
  if (!env) {
    return false;
  }
  // A blocked app has exactly one thing to do and deploying is not it: the
  // block card on the overview carries the explanation and the appeal. The
  // pre-deploy check refuses these anyway, one toast later.
  if (app.block) {
    return false;
  }
  if ([AppStatus.INITIALIZING, AppStatus.PENDING_VERIFICATION, AppStatus.FAILED].includes(app.status)) {
    return false;
  }
  if (env.isPendingPayment || (!env.isFreePlanElegible && env.plan.name.includes('Free'))) {
    return false;
  }
  return hasWritePermission(app, 'Deploy');
};

/**
 * Whether there is a deployment to roll back FROM.
 *
 * Only while the app is Available. A rollback goes from the live deployment
 * to the one before it, and an app with no live deployment — URL expired,
 * deploy failed, still deploying — has nothing to roll back from: the server
 * answers NO_PREVIOUS and the dialog would open on "No deployment is live
 * yet". Those apps redeploy instead.
 *
 * One rule for every place the rollback is offered — the Overview's button,
 * the Action menu and the phone's sheet. The button had it and the two menus
 * did not, so the same app showed the rollback in one place and not another.
 */
export const canRollBack = (app: App): boolean =>
  canRunAppActions(app) && getAppStatusSummary(app).tone === 'ok';

/**
 * Ship the head of the branch, with everything that has to happen around it.
 *
 * A hook rather than a component because two controls trigger the same deploy
 * and must not each carry a copy of it: the Action menu, which is where this
 * lives normally, and the Redeploy button the expired-URL state puts beside
 * the upgrade offer. Each renders its own confirm modal; only the one that
 * was pressed ever has a `pendingDeploy` to show it with.
 */
const useDeployLatestCommit = (app: App) => {
  const mainServiceEnvironment = app.mainService && app.mainService.mainServiceEnvironment;
  const navigate = useNavigate();

  const [isDeploying, setIsDeploying] = useState(false);
  const [pendingDeploy, setPendingDeploy] = useState<{
    serviceEnvironmentId: string;
    runAssistance: RunAssistance;
  } | null>(null);

  const deployLatestCommit = async () => {
    if (!hasWritePermission(app, 'Deploy')) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }

    trackEvent(AmplitudeEvent.DEPLOY_LATEST_COMMIT);
    setIsDeploying(true);
    const toastId = toast.loading('Deploying latest commit...', {
      className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
      icon: <StatusSpinnerSVG width="24px" height="24px" color="text-old-blue" className="animate-spin" />
    });
    try {
      const prepared = await runPreDeploy(
        mainServiceEnvironment?.id as string
      );

      if (prepared.blocked) {
        // The block card on the overview carries the explanation and the
        // appeal; a toast would only repeat it worse.
        toast.dismiss(toastId);
        navigate(`/apps/${app.id}`);
        return;
      }

      if (prepared.needsConfirmation && prepared.runAssistance) {
        toast.dismiss(toastId);
        setPendingDeploy({
          serviceEnvironmentId: mainServiceEnvironment?.id as string,
          runAssistance: prepared.runAssistance,
        });
        return;
      }

      await back4app2.triggerManualDeployment(mainServiceEnvironment?.id as string);
      toast.success('New deployment created!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        id: toastId,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in top-" />
      });
      navigate(`/apps/${app.id}`);
    } catch (err) {
      let errMsg = 'Something went wrong!';
      if (err instanceof AppStatusError || err instanceof UserVerificationPendingError || err instanceof BlockedResourceError || err instanceof PaymentPendingError || err instanceof NotFoundError || err instanceof DeploymentStatusError || err instanceof AuthorizationError) {
        errMsg = err.message;
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE.replace('max-w-xs', 'min-w-xs'),
        id: toastId,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    } finally {
      setIsDeploying(false);
    }
  }

  // The confirm screen is part of the deploy, not of whichever control
  // started it, so it travels with the hook.
  const confirmModal = pendingDeploy && app.mainService?.mainServiceEnvironment ? (
    <PreDeployConfirmModal
      appId={app.id}
      serviceEnvironment={app.mainService.mainServiceEnvironment}
      runAssistance={pendingDeploy.runAssistance}
      onCancel={() => setPendingDeploy(null)}
      onProceed={async () => {
        const { serviceEnvironmentId } = pendingDeploy;
        setPendingDeploy(null);
        await back4app2.triggerManualDeployment(serviceEnvironmentId);
        navigate(`/apps/${app.id}`);
      }}
    />
  ) : null;

  return { deployLatestCommit, isDeploying, confirmModal };
};

/**
 * The app's deploy menu.
 *
 * A menu rather than a button, which is what stood here before the restyle:
 * "Action" with the chevron, and the deploy as its item. It went to a button
 * on the argument that a list of one is machinery — but the button then had
 * to say what it ships in the sidebar's 300px, and the one loud control on
 * the screen became the one that ships a commit rather than the one that
 * keeps the app's address. Named for the category, it is quiet by default and
 * has room to grow the items that were always meant to sit in it.
 *
 * It never says "Redeploy". Renewing an expired URL is `RedeployButton`
 * below, which appears only in that state and beside the offer it competes
 * with.
 */
/**
 * The Action menu's rows, for a menu drawn elsewhere: the phone's "⋯" sheet
 * in the app header. The dialogs the rows open come along, to render once.
 */
export const useAppActionRows = (app: App) => {
  const { deployLatestCommit, isDeploying, confirmModal } = useDeployLatestCommit(app);
  const [isRollbackOpen, setIsRollbackOpen] = useState(false);
  const isUploadApp = app.mainService?.mainServiceEnvironment?.sourceKind === 'UPLOAD';

  const rows = canRunAppActions(app)
    ? [
        {
          label: isDeploying ? 'Deploying…' : isUploadApp ? 'Redeploy latest code' : 'Deploy the latest commit',
          isDisabled: isDeploying,
          onClick: deployLatestCommit,
        },
        ...(canRollBack(app) ? [{
          label: 'Roll back to previous',
          onClick: () => {
            if (!hasWritePermission(app, 'Deploy')) {
              toast.error('You don\'t have permission to trigger deployments.', {
                className: TOAST_SURFACE,
              });
              return;
            }
            setIsRollbackOpen(true);
          },
        }] : []),
      ]
    : [];

  const modals = <>
    {confirmModal}
    <RollbackDialog
      appId={app.id}
      open={isRollbackOpen}
      onClose={() => setIsRollbackOpen(false)}
    />
  </>;

  return { rows, modals };
};

const MyAppActions = ({ app, compact = false }: {
  app: App,
  /** Inline in the mobile header, beside the app name. */
  compact?: boolean,
}) => {
  const { deployLatestCommit, isDeploying, confirmModal } = useDeployLatestCommit(app);
  const [isOpen, setIsOpen] = useState(false);
  const [isRollbackOpen, setIsRollbackOpen] = useState(false);
  // An upload app has no commit: the redeploy builds its latest upload.
  const isUploadApp = app.mainService?.mainServiceEnvironment?.sourceKind === 'UPLOAD';
  const ref = useRef<HTMLDivElement>(null);

  // A menu that outlives a click elsewhere is a menu the customer has to
  // dismiss on purpose.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  if (!canRunAppActions(app)) {
    return null;
  }

  return <>
    <div className={`relative ${compact ? '' : 'w-full'}`} ref={ref}>
      <DsButton
        variant="outline"
        size={compact ? 'md' : 'lg'}
        className={compact ? '' : 'w-full justify-between'}
        isDisabled={isDeploying}
        ariaLabel="App actions"
        ariaExpanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
      >
        {isDeploying ? 'Deploying…' : 'Action'}
        <ChevronDownIcon
          size={14}
          className={`flex-none transition-transform duration-[160ms] ${isOpen ? 'rotate-180' : ''}`}
        />
      </DsButton>

      {isOpen ? (
        /* Under the trigger and at least its width. The compact trigger sits
           at the right edge of the phone header, so there the menu hangs from
           its right side — from the left it ran off the screen. */
        <div className={`absolute z-30 top-[calc(100%+4px)] ${compact ? 'right-0' : 'left-0'} min-w-full rounded-surface border border-navy-400 bg-navy-700 shadow-[0_10px_24px_rgba(0,0,0,.35)] overflow-hidden`}>
          <button
            type="button"
            className="b4-focus w-full px-[18px] py-2.5 text-left text-[length:calc(14px*var(--b4-type,1))] font-semibold text-white whitespace-nowrap hover:bg-navy-600 transition-colors duration-[120ms]"
            onClick={() => {
              setIsOpen(false);
              deployLatestCommit();
            }}
          >
            {isUploadApp ? 'Redeploy latest code' : 'Deploy the latest commit'}
          </button>
          {canRollBack(app) ? (
            <button
              type="button"
              className="b4-focus w-full px-[18px] py-2.5 text-left text-[length:calc(14px*var(--b4-type,1))] font-semibold text-white whitespace-nowrap hover:bg-navy-600 transition-colors duration-[120ms] border-t border-navy-500"
              onClick={() => {
                setIsOpen(false);
                if (!hasWritePermission(app, 'Deploy')) {
                  toast.error('You don\'t have permission to trigger deployments.', {
                    className: TOAST_SURFACE,
                  });
                  return;
                }
                setIsRollbackOpen(true);
              }}
            >
              Roll back to previous
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
    {confirmModal}
    <RollbackDialog
      appId={app.id}
      open={isRollbackOpen}
      onClose={() => setIsRollbackOpen(false)}
    />
  </>;
};

/**
 * The deploy that renews an expired URL, as its own button.
 *
 * Only drawn while the address is gone, and only beside the card that says so
 * — the choice at that moment is between buying an address that stays and
 * renting another one, and those two buttons have to stand together to read
 * as a choice. Outlined green rather than filled: the filled green on this
 * screen is the upgrade, and this is its quieter half.
 */
export const RedeployButton = ({ app }: { app: App }) => {
  const { deployLatestCommit, isDeploying, confirmModal } = useDeployLatestCommit(app);

  if (!canRunAppActions(app)) {
    return null;
  }

  return <>
    <DsButton
      variant="outline-ok"
      size="lg"
      className="w-full"
      title="Redeploy App"
      isDisabled={isDeploying}
      onClick={deployLatestCommit}
    >
      {isDeploying ? 'Deploying…' : 'Redeploy App'}
    </DsButton>
    {confirmModal}
  </>;
};

export default MyAppActions;
